import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  MessageCircle, Instagram, Facebook, MapPin, Phone, Clock, ArrowLeft, ShoppingBag, Clapperboard,
} from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { ReelsGrid } from "@/components/site/Reels";
import api, { fileUrl } from "@/lib/api";
import { waLink, formatBRL, isExpired, productContact } from "@/lib/site";
import SiteLayout from "@/components/site/SiteLayout";
import SmartImage from "@/components/site/SmartImage";
import { CouponCard } from "@/components/site/sections";

const BusinessPage = () => {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reels, setReels] = useState([]);
  const { sections } = useSite();
  const reelsOn = sections.find((s) => s.key === "reels")?.show_on_business !== false;

  useEffect(() => {
    api.get(`/public/businesses/${id}`).then((r) => setB(r.data)).catch(() => setB(false));
    api.get(`/public/products?business_id=${id}`).then((r) => setProducts(r.data)).catch(() => {});
    api.get(`/public/promotions?business_id=${id}`).then((r) => setPromotions(r.data)).catch(() => {});
    api.get(`/public/coupons?business_id=${id}`).then((r) => setCoupons(r.data)).catch(() => {});
    api.get(`/public/reels?business_id=${id}`).then((r) => setReels(r.data.filter((x) => x.video))).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  if (b === false) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Empresa não encontrada.</div></SiteLayout>;
  if (!b) return <SiteLayout><div className="p-10 text-center text-muted-foreground">Carregando...</div></SiteLayout>;

  return (
    <SiteLayout>
      <div className="relative">
        {b.cover ? (
          <img src={fileUrl(b.cover)} alt={b.name} className="w-full h-48 sm:h-64 object-cover" />
        ) : <div className="w-full h-48 sm:h-64 bg-muted" />}
        <Link to="/empresas" className="absolute top-4 left-4 p-2 rounded-full bg-black/40 text-white backdrop-blur" data-testid="business-back">
          <ArrowLeft className="w-5 h-5" />
        </Link>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 -mt-10 relative">
        <div className="bg-card rounded-2xl border border-border shadow-sm p-4 sm:p-6">
          <div className="flex items-start gap-4">
            {b.logo && <img src={fileUrl(b.logo)} alt="" className="w-16 h-16 rounded-2xl object-cover border-2 border-white shadow -mt-12 bg-white" />}
            <div className="flex-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">{b.category}</span>
              <h1 className="text-xl sm:text-2xl font-bold font-heading text-foreground leading-tight">{b.name}</h1>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">{b.description}</p>

          <div className="mt-4 space-y-1.5 text-sm text-foreground">
            {b.address && <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-primary shrink-0" /> {b.address}</div>}
            {b.phone && <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-primary shrink-0" /> {b.phone}</div>}
            {b.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-primary shrink-0" /> {b.hours}</div>}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {b.instagram && <a href={b.instagram} target="_blank" rel="noreferrer" data-testid="business-instagram" className="p-2.5 rounded-full bg-muted hover:bg-muted/70 transition-colors"><Instagram className="w-5 h-5 text-foreground" /></a>}
            {b.facebook && <a href={b.facebook} target="_blank" rel="noreferrer" data-testid="business-facebook" className="p-2.5 rounded-full bg-muted hover:bg-muted/70 transition-colors"><Facebook className="w-5 h-5 text-foreground" /></a>}
          </div>

          {b.whatsapp && (
            <a href={waLink(b.whatsapp, `Olá! Encontrei ${b.name} no Divulga Pocinhos Guia e gostaria de fazer um pedido.`)} target="_blank" rel="noreferrer" data-testid="business-order-wa" className="mt-4 w-full flex items-center justify-center gap-2 bg-secondary text-secondary-foreground font-bold rounded-full py-3 active:scale-95 transition-transform">
              <MessageCircle className="w-5 h-5" /> {b.wa_button_text || "Pedir pelo WhatsApp"}
            </a>
          )}
        </div>

        {promotions.filter((p) => !isExpired(p.valid_until)).length > 0 && (
          <div className="mt-6">
            <h2 className="text-lg font-bold font-heading mb-3">Promoções</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {promotions.filter((p) => !isExpired(p.valid_until)).map((p) => (
                <div key={p.id} data-testid={`business-promo-${p.id}`} className="bg-card rounded-2xl border border-border overflow-hidden flex flex-col">
                  {p.image && <img src={fileUrl(p.image)} alt={p.title} className="w-full h-24 object-cover" />}
                  <div className="p-2 flex flex-col flex-1">
                    <h3 className="text-sm font-semibold line-clamp-1">{p.title}</h3>
                    <div className="flex items-center gap-1 mt-1">
                      {p.old_price && <span className="text-xs line-through text-muted-foreground">{formatBRL(p.old_price)}</span>}
                      <span className="text-primary font-bold">{formatBRL(p.new_price)}</span>
                    </div>
                    {p.details_enabled !== false && (
                      <Link to={`/promocao/${p.id}`} data-testid={`promo-details-${p.id}`} className="mt-2 block text-center text-xs font-semibold text-primary border border-primary/30 rounded-full py-1.5 hover:bg-primary hover:text-primary-foreground transition-colors">
                        {p.details_button_text || "Ver detalhes"}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {products.length > 0 && (
          <div className="mt-6">
            <h2 className="text-lg font-bold font-heading mb-3 flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-primary" /> Cardápio / Produtos</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {products.map((p) => {
                const contact = productContact(p, b);
                const showBtn = p.available !== false && p.contact_enabled !== false && !!contact;
                const btnText = p.contact_button_text || "Ver produto";
                return (
                  <div key={p.id} data-testid={`product-${p.id}`} className={`bg-card rounded-2xl border border-border p-3 flex flex-col gap-3 transition-shadow hover:shadow-md ${p.available === false ? "opacity-60" : ""}`}>
                    <Link to={`/produto/${p.id}`} data-testid={`product-open-${p.id}`} className="flex gap-3 min-w-0">
                      <SmartImage src={p.image} alt={p.name} className="w-20 h-20 rounded-xl object-cover shrink-0" iconClassName="w-6 h-6" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground leading-tight line-clamp-1">{p.name}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{p.description}</p>
                        <span className="font-bold text-primary block mt-2">{formatBRL(p.price)}</span>
                      </div>
                    </Link>
                    <div className="flex items-center justify-between gap-2">
                      {p.available === false ? (
                        <span className="text-xs text-muted-foreground font-semibold">Indisponível</span>
                      ) : (
                        <Link to={`/produto/${p.id}`} className="text-xs text-muted-foreground font-medium underline-offset-2 hover:underline">Ver detalhes</Link>
                      )}
                      {showBtn && (
                        <a href={contact} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} data-testid={`product-order-${p.id}`} className="text-xs font-bold bg-secondary text-secondary-foreground rounded-full px-4 py-1.5 active:scale-95 transition-transform">
                          {btnText}
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {coupons.filter((c) => !isExpired(c.valid_until)).length > 0 && (
          <div className="mt-6 mb-6">
            <h2 className="text-lg font-bold font-heading mb-3">Cupons</h2>
            <div className="flex gap-3 overflow-x-auto hide-scrollbar snap-x-mandatory pb-2">
              {coupons.filter((c) => !isExpired(c.valid_until)).map((c) => <CouponCard key={c.id} c={c} business={b} />)}
            </div>
          </div>
        )}

        {reelsOn && reels.length > 0 && (
          <div className="mt-6 mb-6" data-testid="business-reels">
            <h2 className="text-lg font-bold font-heading mb-3 flex items-center gap-2"><Clapperboard className="w-5 h-5 text-primary" /> Reels</h2>
            <ReelsGrid reels={reels} bizMap={{ [b.id]: b }} size="sm" />
          </div>
        )}
      </div>
    </SiteLayout>
  );
};

export default BusinessPage;
